import { faUtensils } from '@fortawesome/free-solid-svg-icons'
import { bebasNeue } from '@/lib/fonts'
import { LOGO_CANVAS_WIDTH, getLogoInitials, layoutLogoName, normalizeLogoName, textWidthEm } from '@/lib/utils/generatedLogo'

interface Props {
  name?: string | null
  /** `monogram` (iniciales) para tamaños pequeños como el avatar. */
  variant?: 'full' | 'monogram'
  className?: string
}

// Logo provisional con el nombre del bar mientras no suba uno propio. Pinta en
// `currentColor`, así que el color lo decide el CSS de cada superficie.
const GeneratedLogo = ({ name, variant = 'full', className = '' }: Props) => {
  const label = normalizeLogoName(name)
  const classes = `generated-logo ${className}`.trim()

  if (variant === 'monogram') {
    const initials = getLogoInitials(name)
    const fontSize = initials.length > 1 ? 52 : 64
    return (
      <svg className={classes} viewBox='0 0 100 100' width='100%' height='100%' role='img' aria-label={label}>
        <title>{label}</title>
        <text
          className={bebasNeue.className}
          x='50'
          y={50 + fontSize * 0.35}
          fontSize={fontSize}
          textAnchor='middle'
          fill='currentColor'
          textLength={Math.min(80, Math.round(textWidthEm(initials) * fontSize))}
          lengthAdjust='spacingAndGlyphs'
        >
          {initials}
        </text>
      </svg>
    )
  }

  const { lines, fontSize, lineHeight, lineWidths } = layoutLogoName(name)
  const center = LOGO_CANVAS_WIDTH / 2
  // Composición vertical ajustada al contenido: el viewBox se recorta a su
  // alto, así un nombre corto aprovecha cabeceras apaisadas como la del menú.
  const [iconWidth, iconHeight, , , iconPath] = faUtensils.icon
  const iconSize = 70
  const iconTop = 20
  const iconMiddle = iconTop + iconSize / 2
  const capHeight = fontSize * 0.7
  const firstBaseline = iconTop + iconSize + 34 + capHeight
  const textBottom = firstBaseline + (lines.length - 1) * lineHeight
  const rulesTop = textBottom + 36
  const height = Math.round(rulesTop + 37 + 20)
  const iconScale = iconSize / iconHeight

  return (
    <svg className={classes} viewBox={`0 0 ${LOGO_CANVAS_WIDTH} ${height}`} width='100%' height='100%' preserveAspectRatio='xMidYMid meet' role='img' aria-label={label}>
      <title>{label}</title>
      <g fill='currentColor'>
        <path transform={`translate(${center - (iconWidth * iconScale) / 2} ${iconTop}) scale(${iconScale})`} d={Array.isArray(iconPath) ? iconPath.join(' ') : iconPath} />
        {lines.map((line, i) => (
          <text
            key={i}
            className={bebasNeue.className}
            x={center}
            y={Math.round(firstBaseline + i * lineHeight)}
            fontSize={fontSize}
            textAnchor='middle'
            textLength={lineWidths[i]}
            lengthAdjust='spacingAndGlyphs'
          >
            {line}
          </text>
        ))}
      </g>
      <g stroke='currentColor' strokeLinecap='round'>
        <line x1='95' y1={iconMiddle - 10} x2='195' y2={iconMiddle - 10} strokeWidth='4' />
        <line x1='135' y1={iconMiddle + 10} x2='195' y2={iconMiddle + 10} strokeWidth='3' />
        <line x1='305' y1={iconMiddle - 10} x2='405' y2={iconMiddle - 10} strokeWidth='4' />
        <line x1='305' y1={iconMiddle + 10} x2='365' y2={iconMiddle + 10} strokeWidth='3' />
        <line x1='90' y1={rulesTop} x2='410' y2={rulesTop} strokeWidth='6' />
        <line x1='160' y1={rulesTop + 20} x2='340' y2={rulesTop + 20} strokeWidth='4' />
        <line x1='218' y1={rulesTop + 37} x2='282' y2={rulesTop + 37} strokeWidth='3' />
      </g>
    </svg>
  )
}

export default GeneratedLogo
